// The contacts map's ring layout, as arithmetic (no React, no JointJS). Don, stand-up 2026-09-25: "I can't press
// on the dots" — forty accounts on one 170 px ring put the 112 px account cards 27 px apart, so the cards covered
// each other's people. A ring holds only as many cards as fit along it with a gap; the rest go on the next ring
// out. The drawing grows with the firm's book and the canvas fits it (then the wheel zooms in — REQ-109).

export interface RingSlot {
  /** the ring, 0 innermost */
  ring: number
  /** the angle on the ring, radians, 0 at the top */
  angle: number
  /** the ring's radius */
  radius: number
  x: number
  y: number
}

/** A card and the people around it need this much arc before the next card, and this much between rings. */
export const CARD_ARC = 136
export const RING_STEP = 150

/** How many cards a ring of this radius holds without two cards touching. */
export function ringCapacity(radius: number, arc = CARD_ARC): number {
  return Math.max(1, Math.floor((2 * Math.PI * radius) / arc))
}

/**
 * Slots for `count` cards around (cx, cy): the innermost ring at `innerRadius`, each further ring `step` out, each
 * ring filled to its capacity and the last ring spread evenly. Rings are staggered by half a slot so a card on the
 * outer ring sits between two on the inner one.
 */
export function ringLayout(count: number, cx: number, cy: number, innerRadius: number, step = RING_STEP, arc = CARD_ARC): RingSlot[] {
  const slots: RingSlot[] = []
  let placed = 0
  let ring = 0
  while (placed < count) {
    const radius = innerRadius + ring * step
    const capacity = ringCapacity(radius, arc)
    const onThisRing = Math.min(capacity, count - placed)
    const offset = ring % 2 === 1 ? Math.PI / onThisRing : 0
    for (let i = 0; i < onThisRing; i++) {
      const angle = (i / onThisRing) * Math.PI * 2 - Math.PI / 2 + offset
      slots.push({ ring, angle, radius, x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius })
    }
    placed += onThisRing
    ring++
  }
  return slots
}

/** The straight-line distance between two slots. */
export function chord(a: RingSlot, b: RingSlot): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
